import React, { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  Box,
  Button,
  Typography,
  Paper,
  Stack,
  TextField,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { Controller, useFormContext } from "react-hook-form";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { formatFileSize, toBase64 } from "../../../utils/documentUtils";
import PlusIcon from "@mui/icons-material/Add";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ReplayIcon from "@mui/icons-material/Replay";
import { useTranslation } from "react-i18next";
import { DocumentItemType } from "../types";
import dayjs from "dayjs";

const FileUploader = ({
  dataTestId,
  field,
  isFailed,
  setFaildDocuemnts,
}: {
  field: DocumentItemType;
  isFailed: boolean;
  setFaildDocuemnts;
  dataTestId?: string;
}) => {
  const locale = window.localStorage.i18nextLng;
  const { t } = useTranslation();
  const { name, startDate, endDate, label } = field;
  const { setValue, watch, control, register, getValues } = useFormContext();
  const file = watch(name);

  const DATES_SELECTION_MAP = (startDate: boolean, endDate: boolean) => {
    return startDate && endDate
      ? t("fleet_mgt_adding_driver's_documents_select_issue&expiry_dates_title")
      : startDate
      ? t("fleet_mgt_adding_driver's_documents_select_issue_date")
      : endDate
      ? t("fleet_mgt_adding_driver's_documents_select_end_date")
      : null;
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      setFaildDocuemnts((prv) => ({ ...prv, [name]: undefined }));
      const file = acceptedFiles[0];
      const base64 = await toBase64(file);
      const fileObj = {
        name: field.name,
        fileName: file.name,
        ext: file?.name.split(".")?.pop()?.toLowerCase(),
        image: base64?.toString().split(",")[1],
        size: file.size,
      };

      setValue(name, fileObj, { shouldValidate: true });
    },
    [setValue, name]
  );

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    noClick: true, // Prevent click opening the file dialog
    multiple: false, // Allow only one file
  });

  useEffect(() => {
    register(name, {
      required: field.required ? "This document is required" : false,
    });
  }, [register]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          border:
            file && !isFailed && (startDate || endDate) ? "solid #C4C0D3" : "",
          borderRadius: 2,
          padding: 1,
        }}
      >
        <Typography
          variant="h5"
          textAlign="start"
          fontWeight={600}
          fontSize={20}
          mb={1.5}
        >
          {label[locale]}
        </Typography>
        <Paper
          {...getRootProps()}
          elevation={0}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: 1,
            border: "2px dashed ",
            borderColor: `${isFailed ? "#FA3434" : "#C4C0D3"}`,
            cursor: "pointer",
            width: "100%",
            borderRadius: 3,
            boxSizing: "border-box",
            height: 56,
            bgcolor: isFailed ? "#FEF2EF" : "",
          }}
        >
          {" "}
          {file ? (
            <Stack
              direction="row"
              alignContent="center"
              alignItems="center"
              sx={{ width: "100%" }}
            >
              {isFailed ? (
                <WarningAmberIcon color="error" />
              ) : (
                <ArticleOutlinedIcon />
              )}
              <Stack
                justifyContent="start"
                alignItems="start"
                spacing={-1}
                ml={2}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{
                    maxWidth: 200, // Adjust width as needed
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                  }}
                >
                  {file?.fileName}
                </Typography>
                <Stack direction="row">
                  {isFailed && (
                    <Typography
                      variant="subtitle1"
                      color="red"
                      fontWeight={600}
                      sx={{
                        "&::after": {
                          content: '"•"', // Unicode dot character
                          marginX: "4px", // Adjust spacing if needed
                          color: "gray",
                        },
                      }}
                    >
                      {t("fleet_mgt_adding_driver's_documents_failed")}
                    </Typography>
                  )}
                  <Typography variant="subtitle1">
                    {formatFileSize(file?.size)}
                  </Typography>
                </Stack>
              </Stack>
              <Stack
                direction="row"
                sx={{
                  marginLeft: locale !== "fr" ? "" : "auto",
                  marginRight: locale === "ar" ? "auto" : "",
                }}
                alignItems="center"
                gap={1}
              >
                {isFailed && (
                  <>
                    <input {...getInputProps()} />
                    <Button
                      variant="text"
                      onClick={open}
                      sx={{
                        color: "#1A0244",
                        textDecoration: "underline",
                      }}
                      startIcon={<ReplayIcon />}
                      data-testid="retry-driver-document-button"
                    >
                      {t("fleet_mgt_adding_driver's_documents_retry_cta")}
                    </Button>
                  </>
                )}
                <ClearIcon
                  onClick={() => {
                    setValue(name, undefined, { shouldValidate: true });
                  }}
                />
              </Stack>
            </Stack>
          ) : (
            <>
              <input {...getInputProps()} />
              <Typography variant="body1">
                {t("fleet_mgt_driver's_documents_drag_or_drop_files_text")}
              </Typography>
              <Button
                variant="contained"
                onClick={open}
                sx={{
                  "& .MuiButton-startIcon": {
                    mx: 1,
                  },
                }}
                data-testid={dataTestId}
                startIcon={<PlusIcon sx={{ fontSize: 16 }} />}
              >
                {t("fleet_mgt_driver's_documents_upload_file_cta")}
              </Button>
            </>
          )}
        </Paper>
        <Typography
          variant="subtitle1"
          textAlign="start"
          sx={{ color: "#BE352F" }}
        >
          {isFailed && t("fleet_mgt_driver's_documents_upload_error")}
        </Typography>
        {file && (
          <Box>
            {(startDate || endDate) && (
              <Typography
                variant="h5"
                textAlign="start"
                fontSize={20}
                fontWeight={600}
                my={1}
              >
                {DATES_SELECTION_MAP(startDate, endDate)}
              </Typography>
            )}
            <Stack direction="row" gap={3}>
              {startDate && (
                <Controller
                  name={`${name}.start`}
                  control={control}
                  rules={{
                    required: true,
                  }}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(date) => {
                        setValue(
                          name,
                          {
                            ...file,
                            start: date?.toISOString(),
                            end: dayjs(date).isAfter(watch(name)?.end)
                              ? undefined
                              : dayjs(date).isAfter(watch(name)?.end),
                          },
                          { shouldValidate: true }
                        );
                      }}
                      disableFuture
                      maxDate={dayjs(watch(name).end) || undefined}
                      slots={{ textField: TextField }}
                      slotProps={{
                        textField: {
                          placeholder: t(
                            "fleet_mgt_adding_driver's_documents_date_placeholder"
                          ) as string,
                          fullWidth: true,
                          sx: {
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          },
                        },
                      }}
                    />
                  )}
                />
              )}
              {endDate && (
                <Controller
                  name={`${name}.end`}
                  rules={{
                    required: true,
                    validate: (value) =>
                      !watch(name)?.start || value >= watch(name)?.start || " ",
                  }}
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(date) => {
                        setValue(
                          name,
                          { ...file, end: date?.toISOString() },
                          { shouldValidate: true }
                        );
                      }}
                      shouldDisableDate={(date) => {
                        const startDate = watch(name).start;
                        return (
                          startDate && dayjs(date).isBefore(startDate, "day")
                        );
                      }}
                      slots={{ textField: TextField }}
                      slotProps={{
                        textField: {
                          placeholder: t(
                            "fleet_mgt_adding_driver's_documents_date_placeholder"
                          ) as string,
                          fullWidth: true,
                          sx: {
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          },
                        },
                      }}
                    />
                  )}
                />
              )}
            </Stack>
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default FileUploader;
